import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Common } from 'app/model/entity/common';
import { CommonService } from 'app/service/common.service';
import { Helper } from 'app/common/helper';
import { Subscription } from 'rxjs';
import { TicketEditorService } from 'app/service/ticket-editor.service';

@Component({
  selector: 'dialog-reservation-period-setting',
  templateUrl: './dialog-reservation-period-setting.component.html',
  styleUrls: ['./dialog-reservation-period-setting.component.scss']
})
export class DialogReservationPeriodSettingComponent implements OnInit, OnDestroy {
  languageKey: string;
  commonObject: Common;
  Helper = Helper;
  subscriptions: Array<Subscription> = new Array<Subscription>();
  ticketSetDetail: any[] = [];
  formGroup: FormGroup;
  validationErrors: { [key: number]: string } = {};
  isDisplayDescriptionError: boolean = false;
  informationAccount: any;
  saleTicketId: string;
  reservationIntervalSettings: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<DialogReservationPeriodSettingComponent>,
    private translateService: TranslateService,
    private commonService: CommonService,
    private ticketEditorService: TicketEditorService,
    private formBuilder: FormBuilder
  ) {
    this.formGroup = this.formBuilder.group({});

    if (data) {
      this.languageKey = data.languageKey;
      this.ticketSetDetail = data.ticketSetDetail || [];
      this.informationAccount = data.informationAccount;
      this.saleTicketId = data.saleTicketId;
    }
    this.commonObject = this.commonService.getCommonObject();
  }

  ngOnInit(): void {
    if (this.ticketSetDetail && this.ticketSetDetail.length > 0) {
      this.initializeFormControls();
      this.loadReservationSettings();
    } else {
      console.warn('ticketSetDetail is empty or undefined');
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  /**
   * loadReservationSettings
   */
  private loadReservationSettings(): void {
    if (this.informationAccount && this.saleTicketId) {
      this.ticketEditorService.getReservationIntervalSettings(this.informationAccount, this.saleTicketId)?.subscribe({
        next: response => {
          this.updateFormGroup(response);
          this.reservationIntervalSettings = response.reservationIntervalSettings;
        },
        error: error => {
          console.error('API error:', error);
        }
      });
    } else {
      console.warn('Missing informationAccount or saleTicketId');
    }
  }

  /**
   * Create FormControls
   */
  initializeFormControls() {
    this.ticketSetDetail.forEach(item => {
      const priority = item.displayPriority;
      const disabled = this.isFirstFilteredItem(priority);

      const controls = [
        `dependentTicketId_${priority}`,
        `startValue_${priority}`,
        `startUnit_${priority}`,
        `endValue_${priority}`,
        `endUnit_${priority}`
      ];

      controls.forEach(controlName => {
        this.formGroup.addControl(controlName, new FormControl({ value: '', disabled }));
      });
    });
  }

  /**
   * trackByTicketId
   * @param item
   * @returns
   */
  trackByTicketId(item: any): any {
    return item.ticketId;
  }

  /**
   * getDependentTicketControl
   * @param displayPriority
   * @returns
   */
  getDependentTicketControl(displayPriority: number): FormControl {
    return this.formGroup.get(`dependentTicketId_${displayPriority}`) as FormControl;
  }

  /**
   * getAvailableDependentTickets
   * @param currentDisplayPriority
   * @returns
   */
  getAvailableDependentTickets(currentDisplayPriority: number): any[] {
    if (this.isFirstFilteredItem(currentDisplayPriority)) {
      return [];
    }

    return this.ticketSetDetail.filter(
      ticket =>
        ticket.isReserve === true && ticket.displayPriority < currentDisplayPriority && ticket.displayPriority !== currentDisplayPriority
    );
  }

  /**
   * getFilteredTicketSetDetail
   * @returns
   */
  getFilteredTicketSetDetail(): any[] {
    return this.ticketSetDetail?.filter(item => item.isReserve === true) || [];
  }

  /**
   * getMinValue
   * @param type
   * @param displayPriority
   * @returns
   */
  getMinValue(type: 'start' | 'end', displayPriority: number): number {
    const unit = this.formGroup.get(`${type}Unit_${displayPriority}`)?.value;
    if (unit === 'D') return 1;
    if (unit === 'm') return 0;
    return 0;
  }

  /**
   * getMaxValue
   * @param type
   * @param displayPriority
   * @returns
   */
  getMaxValue(type: 'start' | 'end', displayPriority: number): number {
    const unit = this.formGroup.get(`${type}Unit_${displayPriority}`)?.value;
    if (unit === 'D') return 60;
    if (unit === 'm') return 5999;
    return 5999;
  }

  /**
   * hasStartValue
   * @param displayPriority
   * @returns
   */
  hasStartValue(displayPriority: number): boolean {
    const startValue = this.formGroup.get(`startValue_${displayPriority}`)?.value;
    const startUnit = this.formGroup.get(`startUnit_${displayPriority}`)?.value;
    return !!(startValue && startUnit);
  }

  /**
   * hasEndValue
   * @param displayPriority
   * @returns
   */
  hasEndValue(displayPriority: number): boolean {
    const endValue = this.formGroup.get(`endValue_${displayPriority}`)?.value;
    const endUnit = this.formGroup.get(`endUnit_${displayPriority}`)?.value;
    return !!(endValue && endUnit);
  }

  /**
   * onDependentTicketChange
   * @param event
   * @param displayPriority
   */
  onDependentTicketChange(event: Event, displayPriority: number): void {
    const selectedValue = +(event.target as HTMLSelectElement).value;

    const ticket = this.ticketSetDetail.find(item => item.displayPriority === displayPriority);
    const dataSelected = this.ticketSetDetail.find(item => item.ticketId === selectedValue);

    if (ticket && dataSelected) {
      const saleCombinationId = dataSelected.saleCombinationTicketId ?? null;
      ticket.startTargetId = saleCombinationId;
      ticket.endTargetId = saleCombinationId;
    }
  }

  /**
   * onUnitChange
   * @param type
   * @param displayPriority
   */
  onUnitChange(type: 'start' | 'end', displayPriority: number) {
    // Clear validation error wheen change
    if (this.validationErrors[displayPriority]) {
      delete this.validationErrors[displayPriority];
    }
  }

  /**
   * Validate input period - called on blur
   * @param type
   * @param displayPriority
   */
  validatePeriodInput(type: 'start' | 'end', displayPriority: number) {
    if (this.validationErrors[displayPriority]) {
      delete this.validationErrors[displayPriority];
    }
  }

  /**
   * Convert day to minute
   * @param value
   * @param unit
   * @returns
   */
  convertToMinutes(value: number, unit: string): number {
    if (unit === 'D') {
      return value * 24 * 60;
    } else if (unit === 'm') {
      return value;
    }
    return 0;
  }

  /**
   * Select error validation cho period
   * @param displayPriority
   * @returns
   */
  getPeriodValidationError(displayPriority: number): string {
    return this.validationErrors[displayPriority] || '';
  }

  /**
   * Validate formGroup before Save
   * @returns
   */
  validateForm(): boolean {
    let isValid = true;
    this.validationErrors = {};

    this.ticketSetDetail.forEach(item => {
      const priority = item.displayPriority;

      if (this.isFirstFilteredItem(priority)) {
        return;
      }

      const dependentTicket = this.formGroup.get(`dependentTicketId_${priority}`)?.value;
      const startValue = this.formGroup.get(`startValue_${priority}`)?.value;
      const startUnit = this.formGroup.get(`startUnit_${priority}`)?.value;
      const endValue = this.formGroup.get(`endValue_${priority}`)?.value;
      const endUnit = this.formGroup.get(`endUnit_${priority}`)?.value;

      // Convert to boolean properly - check for empty string and null/undefined
      const hasStartValue = (startValue ?? '').toString().trim() !== '';
      const hasStartUnit = (startUnit ?? '').toString().trim() !== '';
      const hasEndValue = (endValue ?? '').toString().trim() !== '';
      const hasEndUnit = (endUnit ?? '').toString().trim() !== '';
      const hasDependentTicket = (dependentTicket ?? '').toString().trim() !== '';

      // 1. Validate có giá trị nhưng không có đơn vị
      if (hasStartValue && !hasStartUnit) {
        this.validationErrors[priority] = this.translateService.instant(
          'dialog-reservation-period-setting.error-message.validate-unit-blank'
        );
        isValid = false;
        return;
      }

      if (hasEndValue && !hasEndUnit) {
        this.validationErrors[priority] = this.translateService.instant(
          'dialog-reservation-period-setting.error-message.validate-unit-blank'
        );
        isValid = false;
        return;
      }

      // 2. Validate có đơn vị nhưng không có giá trị
      if (!hasStartValue && hasStartUnit && startUnit === 'D') {
        this.validationErrors[priority] = this.translateService.instant(
          'dialog-reservation-period-setting.error-message.validate-day-blank'
        );
        isValid = false;
        return;
      }

      if (!hasEndValue && hasEndUnit && startUnit === 'D') {
        this.validationErrors[priority] = this.translateService.instant(
          'dialog-reservation-period-setting.error-message.validate-day-blank'
        );
        isValid = false;
        return;
      }

      if (!hasStartValue && hasStartUnit && endUnit === 'D') {
        this.validationErrors[priority] = this.translateService.instant(
          'dialog-reservation-period-setting.error-message.validate-day-blank'
        );
        isValid = false;
        return;
      }

      if (!hasEndValue && hasEndUnit && endUnit === 'D') {
        this.validationErrors[priority] = this.translateService.instant(
          'dialog-reservation-period-setting.error-message.validate-day-blank'
        );
        isValid = false;
        return;
      }

      if (!hasStartValue && hasStartUnit && startUnit === 'm') {
        this.validationErrors[priority] = this.translateService.instant(
          'dialog-reservation-period-setting.error-message.validate-minute-blank'
        );
        isValid = false;
        return;
      }

      if (!hasEndValue && hasEndUnit && startUnit === 'm') {
        this.validationErrors[priority] = this.translateService.instant(
          'dialog-reservation-period-setting.error-message.validate-minute-blank'
        );
        isValid = false;
        return;
      }
      if (!hasStartValue && hasStartUnit && endUnit === 'm') {
        this.validationErrors[priority] = this.translateService.instant(
          'dialog-reservation-period-setting.error-message.validate-minute-blank'
        );
        isValid = false;
        return;
      }

      if (!hasEndValue && hasEndUnit && endUnit === 'm') {
        this.validationErrors[priority] = this.translateService.instant(
          'dialog-reservation-period-setting.error-message.validate-minute-blank'
        );
        isValid = false;
        return;
      }

      // 3. Validate range (0-5999)
      if (hasStartValue && hasStartUnit && startUnit === 'm') {
        const startNum = parseInt(startValue);
        if (isNaN(startNum) || startNum < 0 || startNum > 5999) {
          this.validationErrors[priority] = this.translateService.instant(
            'dialog-reservation-period-setting.error-message.validate-minute'
          );
          isValid = false;
          return;
        }
      }

      if (hasEndValue && hasEndUnit && endUnit === 'm') {
        const endNum = parseInt(endValue);
        if (isNaN(endNum) || endNum < 0 || endNum > 5999) {
          this.validationErrors[priority] = this.translateService.instant(
            'dialog-reservation-period-setting.error-message.validate-minute'
          );
          isValid = false;
          return;
        }
      }

      // 4. Validate range (1-60)
      if (hasStartValue && hasStartUnit && startUnit === 'D') {
        const startNum = parseInt(startValue);
        if (isNaN(startNum) || startNum < 1 || startNum > 60) {
          this.validationErrors[priority] = this.translateService.instant('dialog-reservation-period-setting.error-message.validate-day');
          isValid = false;
          return;
        }
      }

      if (hasEndValue && hasEndUnit && endUnit === 'D') {
        const endNum = parseInt(endValue);
        if (isNaN(endNum) || endNum < 1 || endNum > 60) {
          this.validationErrors[priority] = this.translateService.instant('dialog-reservation-period-setting.error-message.validate-day');
          isValid = false;
          return;
        }
      }

      // 5. Validate X < Y (start < end)
      if (hasStartValue && hasStartUnit && hasEndValue && hasEndUnit) {
        const startMinutes = this.convertToMinutes(parseInt(startValue), startUnit);
        const endMinutes = this.convertToMinutes(parseInt(endValue), endUnit);

        if (startMinutes >= endMinutes) {
          this.validationErrors[priority] = this.translateService.instant('dialog-reservation-period-setting.error-message.X-smaller-Y');
          isValid = false;
          return;
        }
      }

      // 6. Validate dependent ticket ID
      const hasAnyPeriod = (hasStartValue && hasStartUnit) || (hasEndValue && hasEndUnit);

      if (hasAnyPeriod && !hasDependentTicket) {
        this.isDisplayDescriptionError = true;
        this.validationErrors[priority] = this.translateService.instant(
          'dialog-reservation-period-setting.error-message.validate-dependent-ticket-id'
        );
        isValid = false;
        return;
      }

      // 7. Validate period restrictions
      if (hasDependentTicket && !hasAnyPeriod) {
        this.validationErrors[priority] = this.translateService.instant(
          'dialog-reservation-period-setting.error-message.validate-booking-period-restrictions'
        );
        isValid = false;
        return;
      }
    });

    return isValid;
  }

  /**
   * Clear all inputs on row
   * @param displayPriority
   * @returns
   */
  clearAllInputs(displayPriority: number) {
    if (this.isFirstFilteredItem(displayPriority)) return;

    this.formGroup.get(`dependentTicketId_${displayPriority}`)?.setValue('');
    this.formGroup.get(`startValue_${displayPriority}`)?.setValue('');
    this.formGroup.get(`startUnit_${displayPriority}`)?.setValue('');
    this.formGroup.get(`endValue_${displayPriority}`)?.setValue('');
    this.formGroup.get(`endUnit_${displayPriority}`)?.setValue('');

    // Clear validation error
    delete this.validationErrors[displayPriority];
  }

  /**
   * Clear dependent ticket selection
   * @param displayPriority
   */
  clearDependentTicket(displayPriority: number) {
    this.clearAllInputs(displayPriority);
  }

  /**
   * Save data
   * @returns
   */
  save(): void {
    this.formGroup.updateValueAndValidity();

    if (!this.validateForm()) return;

    const formData = this.formGroup.value;
    const reservationIntervalSettings: any[] = [];

    this.ticketSetDetail.forEach(item => {
      const priority = item.displayPriority;

      const controlMap = {
        dependentTicketId: formData[`dependentTicketId_${priority}`],
        startValue: formData[`startValue_${priority}`],
        startUnit: formData[`startUnit_${priority}`],
        endValue: formData[`endValue_${priority}`],
        endUnit: formData[`endUnit_${priority}`]
      };

      const setting: any = {
        saleCombinationTicketId: item.saleCombinationTicketId
      };

      if (controlMap.dependentTicketId) {
        setting.startIntervalPeriod = controlMap.startValue ? parseInt(controlMap.startValue) : null;
        setting.startIntervalPeriodUnit = controlMap.startUnit ? this.convertFormUnitToApiUnit(controlMap.startUnit) : null;
        setting.endIntervalPeriod = controlMap.endValue ? parseInt(controlMap.endValue) : null;
        setting.endIntervalPeriodUnit = controlMap.endUnit ? this.convertFormUnitToApiUnit(controlMap.endUnit) : null;
        setting.startTargetId = setting.startIntervalPeriod && setting.startIntervalPeriodUnit ? item.startTargetId : null;
        setting.endTargetId = setting.endIntervalPeriod && setting.endIntervalPeriodUnit ? item.endTargetId : null;
      } else {
        Object.assign(setting, {
          startTargetId: null,
          endTargetId: null,
          startIntervalPeriod: null,
          startIntervalPeriodUnit: null,
          endIntervalPeriod: null,
          endIntervalPeriodUnit: null
        });
      }

      reservationIntervalSettings.push(setting);
    });

    const payload = {
      saleTicketId: this.saleTicketId,
      reservationIntervalSettings
    };

    this.ticketEditorService.updateReservationIntervalSettings(this.informationAccount, payload)?.subscribe({
      next: response => {
        this.dialogRef.close({
          success: true,
          payload,
          response
        });
      },
      error: error => {
        console.error('Update failed:', error);
      }
    });
  }

  private convertFormUnitToApiUnit(formUnit: string): string {
    switch (formUnit) {
      case 'D':
        return 'days';
      case 'm':
        return 'minutes';
      default:
        return formUnit;
    }
  }

  private isFirstFilteredItem(displayPriority: number): boolean {
    const filteredItems = this.getFilteredTicketSetDetail();
    const firstItem = filteredItems[0];
    return firstItem && firstItem.displayPriority === displayPriority;
  }

  updateFormGroup(data: any[]) {
    this.ticketSetDetail.forEach(item => {
      const { displayPriority, ticketId } = item;
      const matchedData = data.find(d => d.ticketId === ticketId);
      if (!matchedData) return;

      // Cập nhật các trường dữ liệu trực tiếp trên item
      Object.assign(item, {
        saleCombinationTicketId: matchedData.saleCombinationTicketId
      });

      const convertUnit = (unit: string | null | undefined): string | null => {
        if (unit === 'minutes') return 'm';
        if (unit === 'days') return 'D';
        return null;
      };

      const controlMap: { [key: string]: any } = {
        [`dependentTicketId_${displayPriority}`]: matchedData.startTargetTicketId || matchedData.endTargetTicketId,
        [`startValue_${displayPriority}`]: matchedData.startIntervalPeriod,
        [`startUnit_${displayPriority}`]: convertUnit(matchedData.startIntervalPeriodUnit),
        [`endValue_${displayPriority}`]: matchedData.endIntervalPeriod,
        [`endUnit_${displayPriority}`]: convertUnit(matchedData.endIntervalPeriodUnit)
      };

      const isDisabled = this.isFirstFilteredItem(displayPriority);

      Object.entries(controlMap).forEach(([controlName, value]) => {
        const control = this.formGroup.get(controlName);
        if (control) {
          control.setValue(value);
          isDisabled ? control.disable() : control.enable();
        }
      });
    });
  }
}
